import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PublicShell } from "@/components/public/PublicChrome";
import OnboardingIntro from "@/pages/OnboardingIntro";

/* =============================================================================
 * AUTH — same art direction as landing / features / pricing (manuscript
 * baseline, Amiri for Arabic, Newsreader display, lapis-gold-harakat).
 * src/pages/AuthEn.jsx, routed at /auth.
 *
 * Logic untouched:
 *  - /auth?mode=signup shows OnboardingIntro first; its answers (name, level,
 *    minutes) are POSTed to /onboarding right after register, so the account
 *    is marked onboarded and the placement-quiz page never runs.
 *  - Google signups skip the intro; Protected sends unboarded accounts to
 *    /welcome, which asks the same two questions.
 *  - ?ref= affiliate tracking, ?next= redirect, and the legal consent gate
 *    all behave exactly as before.
 *
 * lucide-react icons dropped on purpose — the page has no other iconography.
 * ========================================================================== */

function mapIntroLevel(levelId) {
  if (levelId === "fluent") return "B1";
  return "A1"; // "beginner", "confused", or skipped
}

function fmtErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
}

export default function AuthEn() {
  const [mode, setMode] = useState(() =>
    new URLSearchParams(window.location.search).has("ref")
      ? "register"
      : "login",
  );
  const initialModeWasSignup =
    mode === "register" ||
    new URLSearchParams(window.location.search).get("mode") === "signup";
  const [showIntro, setShowIntro] = useState(initialModeWasSignup);
  const [introAnswers, setIntroAnswers] = useState({
    level: null,
    minutes: null,
  });
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [legalConsent, setLegalConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const affiliateCode = params.get("ref")?.trim().toUpperCase() || "";
  const nextPath = params.get("next") || "";

  useEffect(() => {
    if (
      !affiliateCode ||
      sessionStorage.getItem(`harf-affiliate-click-${affiliateCode}`)
    )
      return;
    api
      .get(`/affiliate/track?code=${encodeURIComponent(affiliateCode)}`)
      .then(() =>
        sessionStorage.setItem(`harf-affiliate-click-${affiliateCode}`, "1"),
      )
      .catch(() => {});
  }, [affiliateCode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(url, {
        ...form,
        terms_accepted: mode === "register" ? legalConsent : undefined,
        privacy_acknowledged: mode === "register" ? legalConsent : undefined,
        affiliate_code:
          mode === "register" ? affiliateCode || undefined : undefined,
      });
      setToken(data.token);
      setUser(data);

      if (mode === "register") {
        // Finish onboarding here with what the intro collected.
        try {
          await api.post("/onboarding", {
            level: mapIntroLevel(introAnswers.level),
            daily_goal: introAnswers.minutes || 10,
          });
          await refresh();
        } catch {
          // Worst case Protected sends them to /welcome — no dead end.
        }
        nav(nextPath || loc.state?.from || "/dashboard");
      } else if (!data.onboarded) {
        nav("/welcome");
      } else {
        nav(nextPath || loc.state?.from || "/dashboard");
      }
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    if (mode === "register" && !legalConsent) {
      toast.error(
        "Accept the Terms of Service and acknowledge the Privacy Policy to create your account.",
      );
      return;
    }
    if (mode === "register") {
      localStorage.setItem("harf_oauth_legal_consent", "accepted");
      if (affiliateCode)
        localStorage.setItem("harf_oauth_affiliate_code", affiliateCode);
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${window.location.origin}/auth/google`;
  };

  if (showIntro) {
    return (
      <OnboardingIntro
        onComplete={(name, answers) => {
          setIntroAnswers(answers || {});
          setForm((f) => ({ ...f, name }));
          setMode("register");
          setShowIntro(false);
        }}
      />
    );
  }

  const isLogin = mode === "login";

  return (
    <PublicShell className="hf-auth">
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Inter:wght@400;500;600&display=swap');

.hf-auth {
  --paper: #ECEDF1;
  --ink: #14172A;
  --ink-soft: #5D6179;
  --lapis: #1F3FA8;
  --gold: #B0862F;
  --harakat: #B8412E;
  --rule: #C9CCD6;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.hf-auth * { box-sizing: border-box; }
.hf-auth h1, .hf-auth h2 { font-family: 'Newsreader', Georgia, serif; font-weight: 500; letter-spacing: -0.02em; margin: 0; }
.hf-auth p { margin: 0; }
.hf-auth a { color: inherit; }
.hf-auth :focus-visible { outline: 2px solid var(--lapis); outline-offset: 3px; }
.hf-auth .ar { font-family: 'Amiri', 'Noto Naskh Arabic', serif; direction: rtl; }

.hf-auth .a-wrap { max-width: 1060px; margin: 0 auto; padding: 64px 28px 80px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 0; align-items: stretch; }

/* LEFT PANEL */
.hf-auth .a-panel { background: var(--ink); color: var(--paper); padding: 56px 48px; position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: center; }
.hf-auth .a-panel-glyph { position: absolute; bottom: -46px; right: -12px; font-size: 250px; line-height: 1; color: var(--gold); opacity: .13; user-select: none; }
.hf-auth .a-panel h1 { font-size: clamp(32px, 3.6vw, 46px); line-height: 1.06; max-width: 12ch; position: relative; }
.hf-auth .a-panel p { color: #A7ACC4; font-size: 15.5px; margin-top: 18px; max-width: 38ch; position: relative; }
.hf-auth .a-trial { display: inline-block; font-size: 12.5px; color: var(--gold); border: 1px solid var(--gold); border-radius: 2px; padding: 3px 9px; margin-bottom: 22px; position: relative; align-self: flex-start; }
.hf-auth .a-script { margin-top: 42px; font-size: 30px; color: var(--paper); opacity: .45; position: relative; }

/* FORM */
.hf-auth .a-form { background: #fff; border: 1px solid var(--rule); border-left: none; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; }
.hf-auth .a-form h2 { font-size: 28px; }
.hf-auth .a-form .a-sub { color: var(--ink-soft); font-size: 14.5px; margin-top: 6px; margin-bottom: 26px; }
.hf-auth .a-note { border-left: 2px solid var(--gold); padding-left: 16px; margin-bottom: 24px; font-size: 13.5px; color: var(--ink-soft); }
.hf-auth .a-note b { color: var(--ink); font-weight: 600; display: block; font-size: 14px; }
.hf-auth .a-note a { color: var(--lapis); font-weight: 600; text-decoration: none; border-bottom: 1px solid var(--lapis); }
.hf-auth .a-ref { border-left: 2px solid var(--lapis); padding-left: 16px; margin-bottom: 22px; font-size: 13px; color: var(--ink-soft); }
.hf-auth .a-ref strong { color: var(--ink); }

.hf-auth .a-field { width: 100%; border: none; border-bottom: 1px solid var(--rule); background: transparent; padding: 12px 2px; font-size: 15px; font-family: inherit; color: var(--ink); margin-bottom: 4px; border-radius: 0; }
.hf-auth .a-field::placeholder { color: var(--ink-soft); }
.hf-auth .a-field:focus { outline: none; border-bottom-color: var(--lapis); }
.hf-auth .a-submit { width: 100%; margin-top: 26px; background: var(--ink); color: var(--paper); border: 1px solid var(--ink); border-radius: 2px; padding: 14px; font-size: 15px; font-weight: 600; font-family: inherit; cursor: pointer; transition: background .18s ease; }
.hf-auth .a-submit:hover { background: var(--lapis); border-color: var(--lapis); }
.hf-auth .a-submit:disabled { opacity: .5; cursor: default; }
.hf-auth .a-consent { display: flex; gap: 9px; align-items: flex-start; margin-top: 16px; font-size: 12.5px; color: var(--ink-soft); }
.hf-auth .a-consent input { margin-top: 3px; accent-color: var(--lapis); }
.hf-auth .a-consent a { color: var(--ink); text-decoration: underline; }
.hf-auth .a-or { display: flex; align-items: center; gap: 14px; margin: 24px 0; font-size: 12.5px; color: var(--ink-soft); }
.hf-auth .a-or::before, .hf-auth .a-or::after { content: ""; flex: 1; height: 1px; background: var(--rule); }
.hf-auth .a-google { width: 100%; border: 1px solid var(--rule); background: transparent; border-radius: 2px; padding: 13px; font-size: 14.5px; font-weight: 500; font-family: inherit; color: var(--ink); cursor: pointer; transition: border-color .18s ease; }
.hf-auth .a-google:hover { border-color: var(--ink); }
.hf-auth .a-toggle { width: 100%; margin-top: 20px; background: none; border: none; font-family: inherit; font-size: 13.5px; color: var(--ink-soft); cursor: pointer; }
.hf-auth .a-toggle:hover { color: var(--ink); }

@media (max-width: 900px) {
  .hf-auth .a-wrap { grid-template-columns: 1fr; padding: 32px 20px 56px; }
  .hf-auth .a-panel { padding: 40px 28px; }
  .hf-auth .a-panel-glyph { font-size: 170px; bottom: -32px; }
  .hf-auth .a-script { margin-top: 26px; font-size: 24px; }
  .hf-auth .a-form { border-left: 1px solid var(--rule); border-top: none; padding: 36px 28px; }
}
@media (prefers-reduced-motion: reduce) {
  .hf-auth * { animation: none !important; transition: none !important; }
}
            `}</style>

      <div className="a-wrap">
        <aside className="a-panel">
          <span className="a-panel-glyph ar" aria-hidden="true">
            ض
          </span>
          <span className="a-trial">7 days free, no card</span>
          <h1>Read Arabic, for real</h1>
          <p>
            A structured path from your first letter to a certifiable CEFR
            level, instead of another streak to maintain.
          </p>
          <p className="a-script ar" dir="rtl">
            اِقْرَأْ · اُكْتُبْ · تَعَلَّمْ
          </p>
        </aside>

        <div className="a-form">
          <h2>{isLogin ? "Welcome back" : "Create your account"}</h2>
          <p className="a-sub">
            {isLogin
              ? "Pick up where your reviews left off."
              : "Two fields and you are in."}
          </p>

          {!isLogin && (
            <div className="a-note">
              <b>Seven days of everything</b>
              Every lesson, every exercise and the oasis, with no card.{" "}
              <Link to="/pricing">See plans</Link>
            </div>
          )}

          {affiliateCode && (
            <div className="a-ref">
              Partner link · code <strong>{affiliateCode}</strong>
            </div>
          )}

          <form onSubmit={submit}>
            {!isLogin && (
              <input
                required
                data-testid="auth-name"
                placeholder="Name"
                className="a-field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            )}
            <input
              required
              type="email"
              data-testid="auth-email"
              placeholder="Email"
              className="a-field"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              required
              type="password"
              data-testid="auth-password"
              placeholder="Password"
              className="a-field"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <button
              disabled={loading}
              data-testid="auth-submit"
              className="a-submit"
            >
              {loading ? "One moment" : isLogin ? "Log in" : "Create account"}
            </button>
          </form>

          {!isLogin && (
            <label className="a-consent">
              <input
                type="checkbox"
                required
                checked={legalConsent}
                onChange={(e) => setLegalConsent(e.target.checked)}
              />
              <span>
                I agree to the <Link to="/legal/terms">Terms of Service</Link>{" "}
                and <Link to="/legal/purchase-terms">Purchase Terms</Link>, and
                acknowledge the <Link to="/legal/privacy">Privacy Policy</Link>.
              </span>
            </label>
          )}

          <div className="a-or">or</div>

          <button
            data-testid="google-btn"
            onClick={google}
            className="a-google"
          >
            Continue with Google
          </button>

          <button
            data-testid="auth-toggle"
            onClick={() => setMode(isLogin ? "register" : "login")}
            className="a-toggle"
          >
            {isLogin
              ? "No account? Sign up"
              : "Already have an account? Log in"}
          </button>
        </div>
      </div>
    </PublicShell>
  );
}
